// When a held row may show, so chat comes in one row at a time at the rhythm it
// was typed, instead of in clumps.
//
// Rows wait in the feed until their author's cosmetics are known (so a paint
// never pops in after the row shows), and they keep their order, so one new
// chatter's lookup holds the rows behind it. Released all at once, those rows
// landed together: on a busy chat 65 to 86% of rows showed in groups of up to
// 19 while only about 30% arrived grouped (measured 2026-10-21). Here each row
// keeps the gap it had to the row before it, shifted by however far behind the
// overlay is, and the gaps shrink a little while it is behind so it catches up
// instead of staying late.

/** While behind, each gap plays at this fraction of its length, so the delay
 *  shrinks by the rest of every gap (half here) until it is gone. Measured on a
 *  busy chat (2026-10-21): 0.7 kept rows apart but paid a cold start's delay back
 *  too slowly (median wait 1.4 s). */
export const PACE_CATCH_UP = 0.5;
/** A paced gap never plays longer than this. Rows this far apart already read
 *  as one at a time, so on a quiet chat a late row does not hold the next one
 *  back for seconds. */
export const PACE_MAX_GAP_MS = 300;
/** Rows Twitch sent together still enter this far apart while the overlay is
 *  not far behind, so the list never jumps several lines at once. */
export const PACE_MIN_GAP_MS = 40;
/** Past this delay the minimum gap is dropped: a chat faster than one row per
 *  PACE_MIN_GAP_MS would otherwise fall further and further behind. */
export const PACE_MIN_GAP_UNTIL_LAG_MS = 1_000;
/** Never further behind than this: past it, every ready row shows at once. */
export const PACE_MAX_LAG_MS = 3_000;

export interface PaceState {
  /** Whether a paced row has shown yet. */
  started: boolean;
  /** When the last paced row showed. */
  lastShown: number;
  /** When that row arrived. */
  lastArrived: number;
}

export function newPaceState(): PaceState {
  return { started: false, lastShown: 0, lastArrived: 0 };
}

/**
 * The earliest time a ready row that arrived at `arrived` may show. Catch-up
 * rows (history, restored chat, a reconnect's refill) are old already and show
 * at once, without moving the pace.
 */
export function showAt(state: PaceState, arrived: number, catchUp: boolean): number {
  if (catchUp || !state.started) return arrived;
  const gap = Math.max(0, arrived - state.lastArrived);
  const lag = state.lastShown - state.lastArrived;
  if (lag > PACE_MAX_LAG_MS) return arrived;
  let at = lag > 0 ? state.lastShown + Math.min(gap * PACE_CATCH_UP, PACE_MAX_GAP_MS) : arrived;
  if (lag < PACE_MIN_GAP_UNTIL_LAG_MS) at = Math.max(at, state.lastShown + PACE_MIN_GAP_MS);
  return Math.max(arrived, at);
}

/** A paced row showed at `shown`. */
export function noteShown(state: PaceState, arrived: number, shown: number): void {
  state.started = true;
  state.lastShown = shown;
  state.lastArrived = arrived;
}
